import 'plyr/dist/plyr.css';

import posthog from 'posthog-js';

const players: HTMLElement[] = Array.from(document.querySelectorAll('.audioplayer,.videoplayer'));

const MILESTONES = [25, 50, 75, 100];

const expand =
    '<path d="M8 15a.5.5 0 0 1-.5-.5v-13a.5.5 0 0 1 1 0v13a.5.5 0 0 1-.5.5M.146 8.354a.5.5 0 0 1 0-.708l2-2a.5.5 0 1 1 .708.708L1.707 7.5H5.5a.5.5 0 0 1 0 1H1.707l1.147 1.146a.5.5 0 0 1-.708.708zM10 8a.5.5 0 0 1 .5-.5h3.793l-1.147-1.146a.5.5 0 0 1 .708-.708l2 2a.5.5 0 0 1 0 .708l-2 2a.5.5 0 0 1-.708-.708L14.293 8.5H10.5A.5.5 0 0 1 10 8" stroke="currentColor" stroke-width="0.75" />';
const contract =
    '<path d="M8 15a.5.5 0 0 1-.5-.5v-13a.5.5 0 0 1 1 0v13a.5.5 0 0 1-.5.5M0 8a.5.5 0 0 1 .5-.5h3.793L3.146 6.354a.5.5 0 1 1 .708-.708l2 2a.5.5 0 0 1 0 .708l-2 2a.5.5 0 0 1-.708-.708L4.293 8.5H.5A.5.5 0 0 1 0 8m11.707.5 1.147 1.146a.5.5 0 0 1-.708.708l-2-2a.5.5 0 0 1 0-.708l2-2a.5.5 0 0 1 .708.708L11.707 7.5H15.5a.5.5 0 0 1 0 1z" stroke="currentColor" stroke-width="0.75" />';

if (players.length > 0) {
    import('plyr').then(function (plyr) {
        players.map((player) => {
            const newPlayer = new plyr.default(player, {
                autoplay: false,
                ratio: '16:9',
                controls: [
                    'play-large',
                    'play',
                    'progress',
                    'current-time',
                    'duration',
                    'mute',
                    'volume',
                    'captions',
                    'settings',
                    'pip',
                    'airplay',
                    'fullscreen',
                ],
            });

            const mediaType = player.classList.contains('videoplayer') ? 'video' : 'audio';
            const mediaTitle = player.dataset.mediaTitle ?? '';
            const mediaElement = player as HTMLMediaElement;
            const baseProperties = () => ({
                media_type: mediaType,
                media_title: mediaTitle,
                media_url: mediaElement.currentSrc,
            });

            const SEEK_DEBOUNCE_MS = 300;

            let nextMilestoneIndex = 0;
            let lastPosition = 0;
            let seekBurstFrom = 0;
            let seekDebounceTimer: ReturnType<typeof setTimeout> | undefined;
            let hasStarted = false;

            newPlayer.on('play', () => {
                if (hasStarted) {
                    return;
                }
                hasStarted = true;
                posthog.capture('media_started', baseProperties());
            });

            newPlayer.on('ended', () => {
                posthog.capture('media_completed', baseProperties());
            });

            newPlayer.on('timeupdate', () => {
                if (newPlayer.seeking) {
                    return;
                }
                const { currentTime, duration } = newPlayer;
                lastPosition = currentTime;
                if (!duration) {
                    return;
                }
                const percentPlayed = (currentTime / duration) * 100;
                while (
                    nextMilestoneIndex < MILESTONES.length &&
                    percentPlayed >= MILESTONES[nextMilestoneIndex]
                ) {
                    posthog.capture('media_progress', {
                        ...baseProperties(),
                        milestone: MILESTONES[nextMilestoneIndex],
                    });
                    nextMilestoneIndex += 1;
                }
            });

            newPlayer.on('seeking', () => {
                if (seekDebounceTimer === undefined) {
                    seekBurstFrom = lastPosition;
                }
            });

            newPlayer.on('seeked', () => {
                if (seekDebounceTimer !== undefined) {
                    clearTimeout(seekDebounceTimer);
                }
                seekDebounceTimer = setTimeout(() => {
                    const to = newPlayer.currentTime;
                    posthog.capture('media_seeked', {
                        ...baseProperties(),
                        from: seekBurstFrom,
                        to,
                    });
                    lastPosition = to;
                    seekDebounceTimer = undefined;
                }, SEEK_DEBOUNCE_MS);
            });

            const fullScreenButton = newPlayer.elements.buttons.pip;
            if (fullScreenButton) {
                const theatreButton = document.createElement('button');
                theatreButton.classList.add('plyr__controls__item', 'plyr__control');
                const theatreSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                theatreSvg.setAttribute('focusable', 'false');
                theatreSvg.setAttribute('width', '16');
                theatreSvg.setAttribute('height', '16');
                theatreSvg.setAttribute('viewBox', '0 0 16 16');
                theatreSvg.innerHTML = expand;
                const theatreSpan = document.createElement('span');
                theatreSpan.classList.add('plyr__sr-only');
                theatreSpan.textContent = 'Theatre mode';
                theatreButton.appendChild(theatreSvg);
                theatreButton.appendChild(theatreSpan);
                fullScreenButton.insertAdjacentElement('beforebegin', theatreButton);

                const container = newPlayer.elements.container?.parentNode;
                if (container instanceof HTMLElement) {
                    theatreButton.addEventListener('click', () => {
                        if (container.classList.contains('w-8/12')) {
                            theatreSvg.innerHTML = contract;
                        } else {
                            theatreSvg.innerHTML = expand;
                        }
                        container.classList.toggle('w-full');
                        container.classList.toggle('w-8/12');
                    });
                }
            }
        });
    });
}
